import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Body2, RecurringSmallIcon } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useAssigneeTitle } from "~/components/AssigneePicker/useAssigneeTitle";
import { EntityLink } from "~/components/EntityLink";
import { useSecurity } from "~/components/Hooks";
import { ProjectMembersAssigneePicker } from "~/components/ProjectMembersAssigneePicker";
import { showErrorToast } from "~/components/Toast";
import { TooltipCss } from "~/components/TooltipCss/TooltipCss";
import AvatarContainer from "~/components/Users/AvatarContainer";
import { EDIT_RECURRING_TASK_ERROR } from "~/store/action_types";
import { editRecurringTask } from "~/store/actions/actions.recurringTasks";
import { getGhostTaskByUuid } from "~/store/selectors/selector.ghostTasks";

export const CalendarGhostTaskItem = ({ id }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const ghostTask = useSelector((state) => getGhostTaskByUuid(state, id));
  const viewUrl = `/projects/${ghostTask.project_id}`;
  const assigneeTitle = useAssigneeTitle(ghostTask.assignee_id, ghostTask.fake_assignee_name);
  const can = useSecurity();
  const canEdit = can(["edit"], ghostTask);

  const handleChangeAssignee = useCallback(
    async (assigneeId) => {
      const { type } = await dispatch(
        editRecurringTask(ghostTask.project_id, ghostTask?.created_from_recurring_task_id, {
          assignee_id: assigneeId,
        })
      );

      if (type === EDIT_RECURRING_TASK_ERROR) {
        showErrorToast(`${t("Unable to update assignee")}.`);
      }
    },
    [dispatch, ghostTask, t]
  );

  return (
    <EntityLink
      to={viewUrl}
      state={{ openRecurringTasks: true }}
      draggable={false}
      className="tw-no-underline"
    >
      <div className="tw-w-full tw-max-w-full tw-flex tw-h-10 tw-items-center tw-cursor-pointer tw-border-b tw-border-solid tw-border-0 tw-border-[var(--color-theme-300)] tw-justify-between task-list-item hover:tw-opacity-100 ">
        <RecurringSmallIcon
          className="tw-mr-2 tw-ml-1 tw-shrink-0"
          width={24}
          height={24}
        />
        <Body2
          weight="medium"
          className={classNames("tw-truncate tw-flex-1", {
            "tw-text-red-alert": ghostTask?.is_important,
          })}
        >
          {ghostTask.name}
        </Body2>
        {canEdit ? (
          <ProjectMembersAssigneePicker
            projectId={ghostTask.project_id}
            assigneeId={ghostTask.assignee_id}
            fakeAssigneeName={ghostTask?.fake_assignee_name}
            onChange={handleChangeAssignee}
            hiddenFromClient={ghostTask?.is_hidden_form_clients}
            className={classNames({
              "show-on-hover": !ghostTask.assignee_id,
            })}
            position="bottom-end"
          />
        ) : ghostTask.assignee_id ? (
          <TooltipCss
            data-testid="avatar-tooltip-for-clients"
            title={assigneeTitle}
            className="tw-flex tw-items-center"
            position="left"
          >
            <AvatarContainer
              id={ghostTask.assignee_id}
              className="tw-px-1"
            />
          </TooltipCss>
        ) : null}
      </div>
    </EntityLink>
  );
};

CalendarGhostTaskItem.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
